// Shared geometry for the front-yard / MainScene background (bg_main_*). Both the
// owner MainScene and the read-only VisitMainScene use the same art, so they share
// the walkable floor and the stair nodes — the visitor climbs into a friend's house
// exactly where you climb into your own.

// Walkable ground (feet can't cross above this line). Authored in scene edit mode.
export const MAIN_FLOOR_POLYLINE = [
  [1, 541], [532, 530], [536, 612], [651, 619], [659, 609], [694, 613],
  [762, 599], [880, 613], [1103, 565], [1105, 531], [1278, 533],
];

// Stairs up to the house — feet-position nodes from the base (on the ground) to the
// landing. Walking RIGHT/UP climbs to the next node, LEFT/DOWN descends; past the top
// enters the house. Built from the step art.
// Where you appear, in FEET coordinates (the scene converts to a sprite centre through its
// scale/perspective): a fresh arrival stands in the middle of the path; coming out of the
// house you stand at the foot of the steps.
export const MAIN_SPAWN_FEET_Y = 600;
export const MAIN_SPAWN_FROM_HOME = { x: 575, feetY: 627, facing: 3 };   // facing down-left, away from the door
// Arriving at a house — a friend's on a visit, or your own through "Wróć do siebie" — you stand at the foot of the steps
// (owner 2026-10-28: walking up should take you straight in; the mid-path spot was right of where UP mounts the
// stairs, under the door).
export const MAIN_SPAWN_AT_STAIRS = { x: 586, feetY: 627 };

// Where a bike nobody has moved stands in the front yard: parked centres (GAME_HEIGHT 720 - 170). Side-on they
// are 170-174 px long; apart from each other, and the blue one short of the stairs (x 532, where the floor steps
// down), owner G11 2026-10-24.
export const MAIN_BIKE_PARKED_DEFAULTS = {
  pink: { x: 240, y: 550 },
  blue: { x: 430, y: 550 },
};

export const STAIR_NODES = [
  [586, 611], [597, 593], [608, 572], [637, 549], [662, 535],
];

// Levels 4 and 5 were drawn with their own porch: the steps run straight up between two newel posts (x 538-683 on
// lvl 4, 548-678 on lvl 5) and the door stands right of their middle, so the old path walked up the right rail
// (owner 2026-10-31: "I should be more to the left, in the centre of the stairs"). From the same foot, one node per
// tread up the middle, traced on bg_main_day_lvl4/5. Any other level uses the level-1 porch.
// Level 5's flight starts on the stone path at the bottom step (its own floor line, below). At the top you cross the
// landing to the door (x 652-700) before going in (owner 2026-10-31: in the middle of the landing the house opened
// already), standing on its boards (lvl 5: feet y 490-502; at 494 the boots stood on the wall's foot and floated),
// and stopping at x 654: further right the boot runs over the newel post in front of the landing (x ~672).
const STAIR_NODES_BY_LEVEL = {
  4: [[586, 611], [592, 599], [598, 584], [603, 570], [607, 557], [610, 544], [632, 544], [654, 544]],
  5: [[592, 558], [601, 549], [605, 534], [609, 520], [612, 507], [613, 499], [633, 499], [654, 499]],
};
export const stairNodesFor = (level) => STAIR_NODES_BY_LEVEL[level] ?? STAIR_NODES;

// Level 5's yard was drawn anew (owner 2026-10-31: "fit the ground too"): a stone path curves up to the bottom step
// (its edge at feet y ~553), a flower bed fills the space in front of the steps' right half (to y ~590) and the lawn
// on the right starts at y ~575; on the left the lawn runs up to the fence (~548). The level-1 line kept you 60 px
// below the path. The flower beds lower down stay walkable: the art has no path from the porch to the right lawn.
// Level 4's path meets its bottom step where level 1's ground did, so it keeps that line.
const MAIN_FLOOR_BY_LEVEL = {
  5: [[1, 548], [330, 548], [342, 578], [400, 565], [450, 557], [520, 553], [600, 555], [612, 590], [680, 590],
    [692, 575], [1050, 565], [1095, 560], [1105, 540], [1278, 540]],
};
export const mainFloorFor = (level) => MAIN_FLOOR_BY_LEVEL[level] ?? MAIN_FLOOR_POLYLINE;

// The porch spots other scenes send you to (MAIN_SPAWN_AT_STAIRS, MAIN_SPAWN_FROM_HOME) are level 1's; a porch drawn
// elsewhere has its own, on its path left of the little lantern at the steps' foot (x ~585). Any other spot stays.
const PORCH_SPAWNS_BY_LEVEL = {
  5: [[MAIN_SPAWN_AT_STAIRS, { x: 565, feetY: 566 }], [MAIN_SPAWN_FROM_HOME, { x: 556, feetY: 568 }]],
};
export function porchSpawn(level, x, feetY) {
  const hit = (PORCH_SPAWNS_BY_LEVEL[level] ?? []).find(([from]) => from.x === x && from.feetY === feetY);
  return hit ? { ...hit[1] } : { x, feetY };
}
